import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { NodeData } from '@/types/workflow';
import { Target, Copy, Trash2 } from 'lucide-react';

interface ScorerNodeProps extends NodeProps {
  data: NodeData;
}

export const ScorerNode = memo(({ data, selected, id }: ScorerNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  const nodeColor = '#ec4899'; // Pink for AI processing

  const updateNodeData = useCallback((updates: Partial<NodeData>) => {
    const nodes = getNodes();
    const updatedNodes = nodes.map(node => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            ...updates
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [id, getNodes, setNodes]);

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
    }
  }, [id, getNodes, setNodes]);

  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[340px] backdrop-blur-md bg-background/75 border-pink-500/30 transition-all duration-200 ${
          selected 
            ? 'ring-2 ring-pink-500/50 shadow-lg shadow-pink-500/20' 
            : 'shadow-md hover:shadow-lg'
        }`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
              <Target className="w-4 h-4 text-pink-400" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{data.label || 'Scorer'}</div>
              <div className="text-xs text-muted-foreground">Assign numerical scores based on criteria</div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-3">
          <div>
            <Label className="text-xs">Scoring Instructions</Label>
            <Textarea
              placeholder="Score leads from 0-100 based on company size, industry fit, budget indicators, and engagement level..."
              value={data.scoringInstructions || ''}
              onChange={(e) => updateNodeData({ scoringInstructions: e.target.value })}
              className="min-h-[80px] resize-none text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Min Score</Label>
              <Input
                type="number"
                min="0"
                placeholder="0"
                value={data.minScore || 0}
                onChange={(e) => updateNodeData({ minScore: parseInt(e.target.value) })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Max Score</Label>
              <Input
                type="number"
                min="1"
                placeholder="100"
                value={data.maxScore || 100}
                onChange={(e) => updateNodeData({ maxScore: parseInt(e.target.value) })}
                className="h-8"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Scoring Criteria</Label>
            <Textarea
              placeholder="Company size (30%), Industry fit (25%), Budget indicators (25%), Engagement level (20%)"
              value={data.scoringCriteria || ''}
              onChange={(e) => updateNodeData({ scoringCriteria: e.target.value })}
              className="min-h-[60px] resize-none text-xs"
            />
          </div>

          <div>
            <Label className="text-xs">Output Format</Label>
            <Textarea
              placeholder="Return just the numerical score, or include reasoning: {'score': 85, 'reasoning': 'High engagement, good fit'}"
              value={data.outputFormat || ''}
              onChange={(e) => updateNodeData({ outputFormat: e.target.value })}
              className="min-h-[50px] resize-none text-xs"
            />
          </div>

          <div className="p-2 bg-pink-50 dark:bg-pink-950/20 rounded-md border border-pink-200 dark:border-pink-800">
            <div className="text-xs text-pink-700 dark:text-pink-300">
              📊 AI will analyze input data and assign a numerical score based on your criteria
            </div>
          </div>
        </CardContent>
        
        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Left}
          id="input"
          className="custom-handle"
        />
        
        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="scored"
          className="custom-handle"
          style={{
            backgroundColor: '#ec4899',
            boxShadow: '0 2px 8px rgba(236, 72, 153, 0.3)',
          }}
        />
      </Card>
    </div>
  );
});

ScorerNode.displayName = 'ScorerNode';